import type { AssetPool, PoolConfig, UserAssetInfo } from "@apricot-lend/sdk-ts";
import {
  AccountParser,
  ActionWrapper,
  Addresses,
  currentPerPastRateToCurrentPerCurrentRate,
  DECIMAL_MULT,
  fastForwardPositionAmount,
  MINTS,
  nativeRateToValueRate,
  PUBLIC_CONFIG,
  TokenID,
  tokenRateToNativeRate,
} from "@apricot-lend/sdk-ts";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { APRICOT_FINANCE_PROGRAM, MNDE_MINT } from "@stepfinance/common";
import type { LendingCollateralPosition, LendingCollateralPositionMap } from "@stepfinance/lending";
import type { LiquidityPoolMap } from "@stepfinance/liquidity";
import Decimal from "decimal.js";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import type { MarketsContextState } from "../../contexts/market";
import { useMarkets } from "../../contexts/market";
import { APT_MINT } from "../../models/apricot/ids";
import { calculateLPPrice } from "../../utils/liquidity/utils";

const LM_MNDE_MULTIPLIER = new Decimal(0.195);

const getAmountAndRewards = (
  asset: UserAssetInfo,
  parsedPool: AssetPool,
  pool: PoolConfig,
  aptPrice: number,
  tokenPrice: number | null,
  mndePrice: number,
  isDeposit: boolean,
) => {
  const hasMndeReward = pool.tokenId === TokenID.mSOL;
  const mndeAptMultiplierNative = tokenRateToNativeRate(LM_MNDE_MULTIPLIER, TokenID.MNDE, TokenID.APT);
  const assetIndex = isDeposit ? asset.deposit_index : asset.borrow_index;
  let amount = isDeposit ? asset.deposit_amount : asset.borrow_amount;
  const rewardPerYearPerDorB = isDeposit ? parsedPool.reward_per_year_per_d : parsedPool.reward_per_year_per_b;
  const assetPoolIndex = isDeposit ? parsedPool.deposit_index : parsedPool.borrow_index;
  const aptRewardNativeRate = currentPerPastRateToCurrentPerCurrentRate(rewardPerYearPerDorB, assetPoolIndex);

  const isValidTokenPrice = tokenPrice !== null && tokenPrice > 0;

  const rate = isDeposit ? parsedPool.deposit_rate : parsedPool.borrow_rate;
  const aptRewardRate = isValidTokenPrice
    ? nativeRateToValueRate(aptRewardNativeRate, TokenID.APT, pool.tokenId, aptPrice, tokenPrice)
    : 0;

  let totalApy = isDeposit ? rate.add(aptRewardRate) : rate.sub(aptRewardRate);
  if (hasMndeReward && isValidTokenPrice) {
    const mndeRewardRate = nativeRateToValueRate(
      aptRewardNativeRate.mul(mndeAptMultiplierNative),
      TokenID.MNDE,
      pool.tokenId,
      mndePrice,
      tokenPrice,
    );
    totalApy = totalApy.add(mndeRewardRate);
  }

  amount = fastForwardPositionAmount(amount, assetIndex, assetPoolIndex);

  return {
    amount,
    aptRewardRate,
    totalApy,
  };
};

const getApricotPositions = async (
  connection: Connection,
  publicKey: PublicKey,
  priceInUSD: MarketsContextState["priceInUSD"],
  raydiumPoolsMap: LiquidityPoolMap<"Raydium">,
  orcaPoolsMap: LiquidityPoolMap<"Orca">,
  saberPoolsMap: LiquidityPoolMap<"Saber">,
): Promise<LendingCollateralPositionMap> => {
  const actionWrapper = new ActionWrapper(connection, PUBLIC_CONFIG);
  const userInfo = await actionWrapper.getParsedUserInfo(publicKey);
  const collateralPositionMap: LendingCollateralPositionMap = {};
  const aptPrice = priceInUSD(APT_MINT.toBase58()) ?? 0;
  const mndePrice = priceInUSD(MNDE_MINT.toBase58()) ?? 0;

  const addresses = new Addresses(PUBLIC_CONFIG);
  const base_pda = PublicKey.findProgramAddressSync([Buffer.from("2")], APRICOT_FINANCE_PROGRAM)[0];

  if (userInfo) {
    const userAssetInfos: UserAssetInfo[] = [];
    const poolConfigs: PoolConfig[] = [];

    Object.values(PUBLIC_CONFIG.poolConfigs).forEach((poolConfig) => {
      const assetInfo = userInfo.user_asset_info.find((info) => info.pool_id === poolConfig.poolId);
      if (assetInfo) {
        poolConfigs.push(poolConfig);
        userAssetInfos.push(assetInfo);
      }
    });

    const poolAccountKeys = await Promise.all(
      poolConfigs.map(async (pool) => {
        const poolSeedStr = addresses.mintKeyStrToPoolSeedStr(pool.mint.toBase58());
        const assetPoolKey = await PublicKey.createWithSeed(base_pda, poolSeedStr, APRICOT_FINANCE_PROGRAM);
        return assetPoolKey;
      }),
    );
    const poolAccounts = await connection.getMultipleAccountsInfo(poolAccountKeys, "confirmed");

    for (let idx = 0; idx < userAssetInfos.length; idx++) {
      const asset = userAssetInfos[idx];
      let pool: PoolConfig | undefined = undefined;
      try {
        pool = PUBLIC_CONFIG.getPoolConfigByPoolId(asset.pool_id);
      } catch (e) {
        continue;
      }
      const poolAccount = poolAccounts[idx];
      if (!poolAccount) {
        continue;
      }
      const parsedPool = AccountParser.parseAssetPool(poolAccount.data);
      const decimals = DECIMAL_MULT[pool.tokenId];
      const mint = parsedPool.mint_key.toBase58();

      let tokenPrice = priceInUSD(mint);
      if (tokenPrice === null) {
        if (Object.prototype.hasOwnProperty.call(raydiumPoolsMap, mint)) {
          const pool = raydiumPoolsMap[mint];
          tokenPrice = calculateLPPrice(pool.supply, pool.liquidityInUSD);
        } else if (Object.prototype.hasOwnProperty.call(orcaPoolsMap, mint)) {
          const pool = orcaPoolsMap[mint];
          tokenPrice = calculateLPPrice(pool.supply, pool.liquidityInUSD);
        } else if (Object.prototype.hasOwnProperty.call(saberPoolsMap, mint)) {
          const pool = saberPoolsMap[mint];
          tokenPrice = calculateLPPrice(pool.supply, pool.liquidityInUSD);
        }
      }

      if (pool.tokenCategory === "lp" && pool.lpLeftRightTokenId) {
        if (!asset.deposit_amount.isZero()) {
          const { amount, totalApy } = getAmountAndRewards(
            asset,
            parsedPool,
            pool,
            aptPrice,
            tokenPrice,
            mndePrice,
            true,
          );

          const apricotYieldPosition: LendingCollateralPosition = {
            deposit: {
              amount: amount.div(decimals).toNumber(),
              apy: parsedPool.farm_yield.add(totalApy).toNumber(),
              mintA: MINTS[pool.lpLeftRightTokenId[0]].toBase58(),
              mintB: MINTS[pool.lpLeftRightTokenId[1]].toBase58(),
              tokenPrice: tokenPrice ?? 0,
            },
            borrow: {
              amount: 0,
              apy: parsedPool.borrow_rate.toNumber(),
            },
            display: true,
          };
          collateralPositionMap[mint] = apricotYieldPosition;
        }
        continue;
      }

      const collateralPosition: LendingCollateralPosition = {
        deposit: {
          amount: 0,
          apy: parsedPool.deposit_rate.toNumber(),
        },
        borrow: {
          amount: 0,
          apy: parsedPool.borrow_rate.toNumber(),
        },
        display: pool.tokenId !== TokenID.APT,
      };

      if (!asset.deposit_amount.isZero() || !asset.borrow_amount.isZero()) {
        const { amount: depositAmount, totalApy: depositTotalApy } = getAmountAndRewards(
          asset,
          parsedPool,
          pool,
          aptPrice,
          tokenPrice,
          mndePrice,
          true,
        );
        const { amount: borrowAmount, totalApy: borrowTotalApy } = getAmountAndRewards(
          asset,
          parsedPool,
          pool,
          aptPrice,
          tokenPrice,
          mndePrice,
          false,
        );
        collateralPosition.deposit.amount = depositAmount.div(decimals).toNumber();
        collateralPosition.deposit.apy = depositTotalApy.toNumber();
        collateralPosition.borrow.amount = borrowAmount.div(decimals).toNumber();
        collateralPosition.borrow.apy = borrowTotalApy.toNumber();

        collateralPositionMap[mint] = collateralPosition;
      }
    }
  }
  return collateralPositionMap;
};

export const useApricotLending = () => {
  const { marketsInitialised, refreshing, priceInUSD } = useMarkets();
  const { connected, publicKey } = useEnrichedWallet();
  const { connection } = useConnection();
  const { raydiumPools, orcaPools, saberPools } = useLiquidity();

  const [collateralPositionMap, setCollateralPositionMap] = useState<LendingCollateralPositionMap>({});

  useEffect(() => setCollateralPositionMap({}), [connected]);

  useEffect(() => {
    const marketsRefreshFinished = !refreshing;
    const liquidityReady =
      Object.keys(raydiumPools).length > 0 && Object.keys(orcaPools).length > 0 && Object.keys(saberPools).length > 0;

    if (connected && publicKey && marketsInitialised && marketsRefreshFinished && liquidityReady) {
      void getApricotPositions(connection, publicKey, priceInUSD, raydiumPools, orcaPools, saberPools).then(
        (apricotPositions) => {
          setCollateralPositionMap(apricotPositions);
        },
      );
    }
  }, [
    connected,
    connection,
    marketsInitialised,
    raydiumPools,
    orcaPools,
    saberPools,
    publicKey,
    refreshing,
    priceInUSD,
  ]);

  return { collateral: collateralPositionMap, yieldFarms: {} };
};
